import { useState, useMemo } from "react";
import {
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Task, BoardColumn } from "../types";

interface UseTaskDragDropArgs {
  columns: BoardColumn[];
  listlessTasks: Task[];
  matchingTaskIds: Set<string>;
  // A collapsed list's cards aren't on screen, so they aren't sortable.
  isCollapsed: (listId: string | undefined) => boolean;
  updateTask: (id: string, updates: Partial<Task>) => void;
  updateTaskOrder: (id: string, order: number) => void;
}

interface UseTaskDragDropReturn {
  sensors: ReturnType<typeof useSensors>;
  collisionDetection: typeof closestCenter;
  sortingStrategy: typeof verticalListSortingStrategy;
  activeDragTask: Task | null;
  overTaskId: string | null;
  overEndZoneListId: string | null;
  allSortableIds: string[];
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
}

export function useTaskDragDrop({
  columns,
  listlessTasks,
  matchingTaskIds,
  isCollapsed,
  updateTask,
  updateTaskOrder,
}: UseTaskDragDropArgs): UseTaskDragDropReturn {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const [activeDragTask, setActiveDragTask] = useState<Task | null>(null);
  const [overTaskId, setOverTaskId] = useState<string | null>(null);
  // listId of the end-zone currently being hovered. Uses the sentinel
  // "listless" for the standalone-tasks area (where list_id will be set to
  // null on drop), and the actual list ID otherwise.
  const [overEndZoneListId, setOverEndZoneListId] = useState<string | null>(
    null,
  );

  const allSortableIds = useMemo(
    () => [
      ...columns.flatMap((c) =>
        isCollapsed(c.listId)
          ? []
          : c.tasks.filter((t) => matchingTaskIds.has(t.id)).map((t) => t.id),
      ),
      ...listlessTasks
        .filter((t) => matchingTaskIds.has(t.id))
        .map((t) => t.id),
    ],
    [columns, listlessTasks, matchingTaskIds, isCollapsed],
  );

  const clearDragState = () => {
    setActiveDragTask(null);
    setOverTaskId(null);
    setOverEndZoneListId(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current;
    if (data?.type === "task") {
      setActiveDragTask(data.task as Task);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const overData = event.over?.data.current;
    if (activeDragTask && overData?.type === "task") {
      setOverTaskId((event.over?.id as string) ?? null);
      setOverEndZoneListId(null);
    } else if (activeDragTask && overData?.type === "end-zone") {
      setOverTaskId(null);
      setOverEndZoneListId(
        overData.listId === null ? "listless" : (overData.listId as string),
      );
    } else {
      setOverTaskId(null);
      setOverEndZoneListId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    clearDragState();

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (activeData?.type === "task" && overData?.type === "task") {
      // Task reorder (same or different list)
      const draggedTask = activeData.task as Task;
      const targetTask = overData.task as Task;

      const isDifferentList = draggedTask.list_id !== targetTask.list_id;

      // Get visible tasks from the target list (where we're dropping). The
      // standalone listless tasks live outside `columns` so look them up
      // separately when the drop target is listless.
      const targetListTasks = (
        targetTask.list_id === null
          ? listlessTasks
          : columns.find((c) => c.listId === targetTask.list_id)?.tasks || []
      ).filter((t) => matchingTaskIds.has(t.id));

      const targetIndex = targetListTasks.findIndex(
        (t) => t.id === targetTask.id,
      );
      if (targetIndex === -1) return;

      let newOrderPosition: number;
      if (targetIndex === 0) {
        // Dropping at the beginning of the target list
        newOrderPosition = targetTask.order_position - 50;
      } else {
        const prev = targetListTasks[targetIndex - 1];
        newOrderPosition = prev
          ? (prev.order_position + targetTask.order_position) / 2
          : targetTask.order_position - 50;
      }

      if (isDifferentList) {
        // Move task to different list
        updateTask(draggedTask.id, {
          ...draggedTask,
          list_id: targetTask.list_id,
          order_position: Math.floor(newOrderPosition),
        });
      } else {
        // Reorder within same list
        const listTasks = targetListTasks;
        const draggedIndex = listTasks.findIndex(
          (t) => t.id === draggedTask.id,
        );
        if (draggedIndex === -1) return;

        if (draggedIndex < targetIndex) {
          const next = listTasks[targetIndex + 1];
          newOrderPosition = next
            ? (targetTask.order_position + next.order_position) / 2
            : targetTask.order_position + 100;
        } else {
          // Already calculated above for targetIndex === 0, otherwise recalculate
          const prev = listTasks[targetIndex - 1];
          newOrderPosition = prev
            ? (prev.order_position + targetTask.order_position) / 2
            : targetTask.order_position - 50;
        }

        updateTaskOrder(draggedTask.id, Math.floor(newOrderPosition));
      }
    } else if (activeData?.type === "task" && overData?.type === "end-zone") {
      // Dropped on the end zone — insert after the last task in that list.
      // A targetListId of null means the standalone listless area.
      const draggedTask = activeData.task as Task;
      const targetListId = overData.listId as string | null;

      const targetTasks =
        targetListId === null
          ? listlessTasks
          : columns.find((c) => c.listId === targetListId)?.tasks;
      if (!targetTasks) return;

      const visibleColumnTasks = targetTasks.filter((t) =>
        matchingTaskIds.has(t.id),
      );
      const lastTask = visibleColumnTasks[visibleColumnTasks.length - 1];
      const newOrderPosition = lastTask ? lastTask.order_position + 100 : 100;

      if (draggedTask.list_id === targetListId) {
        // Same list: just reorder to end
        updateTaskOrder(draggedTask.id, Math.floor(newOrderPosition));
      } else {
        // Different list: move and reorder
        updateTask(draggedTask.id, {
          ...draggedTask,
          list_id: targetListId,
          order_position: Math.floor(newOrderPosition),
        });
      }
    }
  };

  const handleDragCancel = () => {
    clearDragState();
  };

  return {
    sensors,
    collisionDetection: closestCenter,
    sortingStrategy: verticalListSortingStrategy,
    activeDragTask,
    overTaskId,
    overEndZoneListId,
    allSortableIds,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
  };
}
